import React from 'react';
import PropTypes from 'prop-types';
import {
  TextInput,
  TextArea,
  ButtonWithIcon,
  HeaderTitle,
  Divider,
  LanguageTabs
} from '@/components/atoms';
import DisplayImageUploadField from './DisplayImageUploadField';
import ValidationErrorMessage from './ValidationErrorMessage';
import styles from './ActivityGameDisplay.module.scss';

const GameDisplayMetadataSection = ({
  languages,
  activeTabIndex,
  setActiveTabIndex,
  currentLangData,
  updateLangField,
  gameData,
  handleGameImageUpload,
  handleGameImageDelete,
  handleLogoImageUpload,
  handleLogoImageDelete,
  gameImageProgress,
  logoImageProgress,
  gameImageValidationError = '',
  logoImageValidationError = '',
  handleGameImageFileError,
  handleLogoImageFileError,
  setShowGuidelines,
  sectionTestId = 'game-display-section',
  showSectionHeader = true
}) => {
  const imageUploadContainerClass = `max-w-[380px] flex-1 rounded-lg border border-[color:var(--color-border-default)] p-3 ${styles.imageUploadContainer}`;

  return (
    <div
      className='w-full rounded-lg shadow-[0_0_24px_rgba(0,0,0,0.08)]'
      style={{backgroundColor: 'var(--color-background-layer-01)'}}
      data-testid={sectionTestId}>
      {showSectionHeader && (
        <>
          <div className='px-4 py-4'>
            <HeaderTitle
              title='Game Display'
              className='text-[16px] font-bold leading-[22px] text-[#FFFFFF]'
            />
            <p className='-mb-4 text-[14px] font-normal leading-[20px] text-[#DADADA]'>
              How the game looks on PepsiConnect Games
            </p>
          </div>

          <Divider className='mx-3 my-0 border-0 border-b' />
        </>
      )}

      <div className='flex flex-col gap-4 px-4 py-3'>
        <div className='flex items-center gap-4'>
          {/* TODO: Restore Download Sample Image when sample download is implemented
          <ButtonWithIcon
            id='download-sample-image'
            handleButton={() => {}}
            uiSchema={{
              title: 'Download Sample Ima...',
              variant: 'tertiary',
              size: 'small',
              isDark: true,
              iconTrailing: 'download'
            }}
          />
          */}
          <ButtonWithIcon
            id='view-guideline'
            iconLeading='open_in_full'
            handleButton={() => setShowGuidelines(true)}
            uiSchema={{
              title: 'View Guideline',
              variant: 'tertiary',
              size: 'small',
              isDark: true
            }}
          />
        </div>

        <div className='flex gap-4'>
          <DisplayImageUploadField
            label='Game Image*'
            imageUri={gameData.gameImageUri}
            progress={gameImageProgress}
            validationError={gameImageValidationError}
            onUpload={handleGameImageUpload}
            onDelete={handleGameImageDelete}
            onFileError={handleGameImageFileError}
            testId='game-image-upload'
            validationErrorTestId='game-image-validation-error'
            containerClassName={imageUploadContainerClass}
            errorPlacement='external'
            wrapperClassName='max-w-[380px] flex-1'
          />
          <DisplayImageUploadField
            label='Logo Image*'
            imageUri={gameData.logoImageUri}
            progress={logoImageProgress}
            validationError={logoImageValidationError}
            onUpload={handleLogoImageUpload}
            onDelete={handleLogoImageDelete}
            onFileError={handleLogoImageFileError}
            testId='logo-image-upload'
            validationErrorTestId='logo-image-validation-error'
            containerClassName={imageUploadContainerClass}
            errorPlacement='external'
            wrapperClassName='max-w-[380px] flex-1'
          />
        </div>
        <div className='flex gap-4'>
          <div className='max-w-[380px] flex-1'>
            <ValidationErrorMessage
              error={gameImageValidationError}
              testId='game-image-validation-error'
            />
          </div>
          <div className='max-w-[380px] flex-1'>
            <ValidationErrorMessage
              error={logoImageValidationError}
              testId='logo-image-validation-error'
            />
          </div>
        </div>

        <LanguageTabs
          languages={languages}
          showDivider={true}
          activeTabIndex={activeTabIndex}
          setActiveTabIndex={(index) => setActiveTabIndex(index)}
          testIdPrefix='title-language-tab'
          dividerClassName='-mt-4 mb-0 border-0 border-b'
        />
        <div className='flex gap-4'>
          <div className='max-w-[380px] flex-1'>
            <TextInput
              value={currentLangData.title}
              onChange={(val) => updateLangField('title', val)}
              label='Title*'
              isDark
              maxLength={55}
              placeholderText='Title'
              id='display-title'
              showLabelIcon
              infoIconTooltip='Max 55 characters'
            />
          </div>
          <div className='max-w-[380px] flex-1'>
            <TextInput
              value={currentLangData.tagDescription}
              onChange={(val) => updateLangField('tagDescription', val)}
              label='Tag Description*'
              isDark
              maxLength={85}
              placeholderText='Tag Description'
              id='display-tag-description'
            />
          </div>
        </div>

        <div className='flex gap-4'>
          <div className={`max-w-[380px] flex-1 ${styles.textAreaWrapper}`}>
            <TextArea
              value={currentLangData.gameDescription}
              updateValue={(val) => updateLangField('gameDescription', val)}
              label='Game Description'
              uiSchema={{
                isDark: true,
                placeholder: 'Game Description',
                maxLength: 500,
                showToolbar: true,
                showCharacterCount: true
              }}
              id='game-description'
              trim={false}
            />
          </div>
          <div className={`max-w-[380px] flex-1 ${styles.textAreaWrapper}`}>
            <TextArea
              value={currentLangData.howToPlayRule}
              updateValue={(val) => updateLangField('howToPlayRule', val)}
              label='How to Play Rule'
              uiSchema={{
                isDark: true,
                placeholder: 'How to Play Rule',
                maxLength: 500,
                showToolbar: true,
                showCharacterCount: true
              }}
              id='how-to-play-rule'
              trim={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

GameDisplayMetadataSection.propTypes = {
  languages: PropTypes.array.isRequired,
  activeTabIndex: PropTypes.number.isRequired,
  setActiveTabIndex: PropTypes.func.isRequired,
  currentLangData: PropTypes.object.isRequired,
  updateLangField: PropTypes.func.isRequired,
  gameData: PropTypes.object.isRequired,
  handleGameImageUpload: PropTypes.func.isRequired,
  handleGameImageDelete: PropTypes.func.isRequired,
  handleLogoImageUpload: PropTypes.func.isRequired,
  handleLogoImageDelete: PropTypes.func.isRequired,
  gameImageProgress: PropTypes.number,
  logoImageProgress: PropTypes.number,
  gameImageValidationError: PropTypes.string,
  logoImageValidationError: PropTypes.string,
  handleGameImageFileError: PropTypes.func,
  handleLogoImageFileError: PropTypes.func,
  setShowGuidelines: PropTypes.func.isRequired,
  sectionTestId: PropTypes.string,
  showSectionHeader: PropTypes.bool
};

export default React.memo(GameDisplayMetadataSection);
